/* Het logboek: notities, taken, mijlpalen, inspecties, nieuwe bestandsversies en
 * gekoppelde mail in één tijdlijn, met één invoerveld voor een notitie, taak of
 * mijlpaal. Vervangt de tabs Tijdlijn, Notities en To-do's van de oude pagina.
 * Open taken staan bovenaan en zijn met één klik af te vinken. */

import { useState } from 'react';
import { CheckSquare, ClipboardCheck, FileText, Flag, History, Mail, StickyNote, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { LogItem, Logboek as LogboekData, LogSoort } from '../data/project';
import { useLogboekActies } from '../hooks';
import { datum } from './opmaak';

const ICOON: Record<LogSoort, { icoon: typeof FileText; klasse: string; label: string }> = {
  notitie: { icoon: StickyNote, klasse: 'bg-slate-100 text-slate-700', label: 'Notitie' },
  taak: { icoon: CheckSquare, klasse: 'bg-green-50 text-green-700', label: 'Taak afgerond' },
  mijlpaal: { icoon: Flag, klasse: 'bg-purple-50 text-purple-700', label: 'Mijlpaal' },
  versie: { icoon: History, klasse: 'bg-blue-50 text-blue-700', label: 'Bestand' },
  inspectie: { icoon: ClipboardCheck, klasse: 'bg-amber-50 text-amber-800', label: 'Inspectie' },
  mail: { icoon: Mail, klasse: 'bg-sky-50 text-sky-700', label: 'Mail' },
};

const EERST = 8;

function Regel({ item }: { item: LogItem }) {
  const [open, setOpen] = useState(false);
  const i = ICOON[item.soort];
  const Icoon = i.icoon;
  const lang = (item.tekst?.length ?? 0) > 180;
  return (
    <li className="grid grid-cols-[24px_minmax(0,1fr)] gap-3 border-b py-2.5 text-sm last:border-b-0">
      <span className={cn('mt-0.5 flex h-6 w-6 items-center justify-center rounded-full', i.klasse)} title={i.label}>
        <Icoon className="h-3.5 w-3.5" aria-hidden />
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs text-muted-foreground">
          {datum(item.datum)} · {i.label.toLowerCase()}
          {item.soort === 'mijlpaal' && item.status === 'completed' && <span className="text-green-700"> · afgerond</span>}
        </span>
        <span className="break-words font-medium leading-snug">{item.titel}</span>
        {item.tekst && (
          <p className={cn('m-0 whitespace-pre-line break-words text-[13px] leading-relaxed text-muted-foreground', !open && lang && 'line-clamp-2')}>
            {item.tekst}
          </p>
        )}
        {lang && (
          <button type="button" onClick={() => setOpen(!open)} className="self-start text-xs font-medium text-foreground/70 hover:text-foreground">
            {open ? 'Minder' : 'Meer'}
          </button>
        )}
      </div>
    </li>
  );
}

export function Logboek({ projectId, logboek, magNotities }: { projectId: string; logboek: LogboekData; magNotities: boolean }) {
  const { toast } = useToast();
  const acties = useLogboekActies(projectId);
  const [tekst, setTekst] = useState('');
  const [soort, setSoort] = useState<'notitie' | 'taak' | 'mijlpaal'>(magNotities ? 'notitie' : 'taak');
  const [datumMijlpaal, setDatumMijlpaal] = useState(() => new Date().toISOString().slice(0, 10));
  const [alles, setAlles] = useState(false);
  const bezig = acties.notitie.isPending || acties.taak.isPending || acties.mijlpaal.isPending;

  const opslaan = () => {
    const t = tekst.trim();
    if (t.length < 2) return;
    const klaar = { onSuccess: () => setTekst(''), onError: (e: unknown) => toast({ title: `${soort[0]!.toUpperCase()}${soort.slice(1)} niet opgeslagen`, description: (e as Error).message, variant: 'destructive' }) };
    if (soort === 'mijlpaal') acties.mijlpaal.mutate({ titel: t, datum: datumMijlpaal }, klaar);
    else (soort === 'notitie' ? acties.notitie : acties.taak).mutate(t, klaar);
  };

  const zichtbaar = alles ? logboek.items : logboek.items.slice(0, EERST);

  return (
    <section id="logboek" aria-labelledby="pagina-logboek" className="flex scroll-mt-14 flex-col gap-3.5">
      <h2 id="pagina-logboek" className="text-lg font-semibold">Logboek</h2>
      <div className="flex flex-col gap-3 rounded-xl border bg-background p-4">
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => { e.preventDefault(); opslaan(); }}
        >
          <label>
            <span className="sr-only">{`Nieuwe ${soort}`}</span>
            <Input
              value={tekst}
              onChange={(e) => setTekst(e.target.value)}
              placeholder={soort === 'notitie' ? 'Schrijf een notitie…' : soort === 'taak' ? 'Wat moet er gebeuren?' : 'Welke mijlpaal? (bijv. contract getekend)'}
              className="h-10 bg-muted/40"
            />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {soort === 'mijlpaal' && (
              <label>
                <span className="sr-only">Datum van de mijlpaal</span>
                <Input type="date" value={datumMijlpaal} onChange={(e) => setDatumMijlpaal(e.target.value)} className="h-9 w-[150px]" />
              </label>
            )}
            <div role="group" aria-label="Soort" className="flex gap-0.5 rounded-lg bg-muted p-[3px] text-[13px]">
              {(['notitie', 'taak', 'mijlpaal'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={soort === s}
                  disabled={s === 'notitie' && !magNotities}
                  title={s === 'notitie' && !magNotities ? 'Notities zijn alleen voor admins' : undefined}
                  onClick={() => setSoort(s)}
                  className={cn('h-8 rounded-md px-2.5 capitalize disabled:opacity-40', soort === s ? 'bg-background font-semibold shadow-sm' : 'text-muted-foreground')}
                >
                  {s}
                </button>
              ))}
            </div>
            <Button type="submit" size="sm" disabled={bezig || tekst.trim().length < 2} className="ml-auto h-9">
              {bezig && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Toevoegen
            </Button>
          </div>
        </form>

        {logboek.openTaken.length > 0 && (
          <div className="flex flex-col gap-1 rounded-lg border bg-muted/30 px-4 py-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Open taken</span>
            <ul className="flex flex-col">
              {logboek.openTaken.map((t) => (
                <li key={t.id} className="flex items-center gap-3 py-1.5 text-sm">
                  <Checkbox
                    id={`taak-${t.id}`}
                    disabled={acties.afronden.isPending}
                    onCheckedChange={() => acties.afronden.mutate(t.id)}
                  />
                  <label htmlFor={`taak-${t.id}`} className="flex-1 cursor-pointer">{t.title}</label>
                  {t.due_date && <span className="text-xs text-muted-foreground">voor {datum(t.due_date)}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {logboek.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nog niets in het logboek.</p>
        ) : (
          <ol className="m-0 flex list-none flex-col p-0">
            {zichtbaar.map((item) => <Regel key={item.id} item={item} />)}
          </ol>
        )}
        {logboek.items.length > EERST && (
          <Button variant="ghost" size="sm" className="self-start" onClick={() => setAlles(!alles)}>
            {alles ? 'Minder tonen' : `Alles tonen (${logboek.items.length})`}
          </Button>
        )}
      </div>
    </section>
  );
}
